import MonthRangeSelector from '@/components/MonthRangeSelector'
import Paper from '@/components/Paper'
import { DataTableCustomized } from '@/components/ui/data-table-customized'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { formatCurrency, formatNumeral } from '@/utils/function'
import dayjs from 'dayjs'
import { BarChart3, Calendar, Download, Hash } from 'lucide-react'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Typogaphy } from 'vnetwork-ui'
import {
    exportEmployeePerformances,
    getEmployeePerformances,
    getMonthlyAllocatedRevenue,
} from '../../services'
import { MonthlyAllocatedRevenue, SalesTargetFilters } from '../../types'
import ActualTarget from './ActualTarget'
import DensityChart from './DensityChart'
import DensityTargetChart from './DensityTargetChart'
import MonthlyRevenueTrends from './MonthlyRevenueTrends'
import MonthlyServiceBreakdown from './MonthlyServiceBreakdown'
import AchievementByPaymentDate from './RevenueByPaymentDate/AchievementByPaymentDate'
import RevenueByPaymentDate from './RevenueByPaymentDate'
import RevenueTarget from './RevenueTarget'
import ServiceBreakdown from './ServiceBreakdown'
import { usePerformanceTableColumns } from './use-performance-table-columns'

export type RevenueType = 'renewal' | 'new_sale' | 'upsell'
export type ViewMode = 'numbers' | 'progress'

interface DashboardProps {
    filters: SalesTargetFilters
}

const Dashboard = ({ filters }: DashboardProps) => {
    const { t } = useTranslation()
    const [selectedRevenueTypes, setSelectedRevenueTypes] = useState<
        RevenueType[]
    >(['renewal', 'new_sale', 'upsell'])
    const [compareRange, setCompareRange] = useState<{
        gte: string | null
        lte: string | null
    }>({ gte: null, lte: null })
    const [showCompareSelector, setShowCompareSelector] = useState(false)
    const [viewMode, setViewMode] = useState<ViewMode>('numbers')

    const hasComparison = !!compareRange.gte && !!compareRange.lte

    const handleAddComparison = () => {
        if (filters.gte && filters.lte) {
            const gteDate = dayjs(filters.gte)
            const lteDate = dayjs(filters.lte)
            setCompareRange({
                gte: gteDate.subtract(1, 'year').format('YYYY-MM-DD'),
                lte: lteDate.subtract(1, 'year').format('YYYY-MM-DD'),
            })
            setShowCompareSelector(true)
        }
    }

    const handleRemoveComparison = () => {
        setCompareRange({ gte: null, lte: null })
        setShowCompareSelector(false)
    }

    const { data, renderState: renderTopState } = useQueryWithHandler({
        queryKey: [
            QUERY_KEYS.REPORTS_BUSINESS_TARGETS,
            'branch',
            filters.gte,
            filters.lte,
            filters.employee_id,
        ],
        queryFn: () =>
            getEmployeePerformances({
                gte: filters.gte,
                lte: filters.lte,
                employee_id: filters.employee_id,
            }),
        enabled: !!filters.gte && !!filters.lte,
    })

    // Comparison data
    const { data: comparisonData } = useQueryWithHandler({
        queryKey: [
            QUERY_KEYS.REPORTS_BUSINESS_TARGETS,
            'branch-comparison',
            compareRange.gte,
            compareRange.lte,
            filters.employee_id,
        ],
        queryFn: () =>
            getEmployeePerformances({
                gte: compareRange.gte!,
                lte: compareRange.lte!,
                employee_id: filters.employee_id,
            }),
        enabled: hasComparison,
    })

    // Monthly allocated revenue for monthly breakdown columns
    const { data: monthlyAllocatedData } = useQueryWithHandler({
        queryKey: [
            QUERY_KEYS.REPORTS_BUSINESS_TARGETS,
            'monthly-allocated-revenue',
            filters.gte,
            filters.lte,
            filters.employee_id,
        ],
        queryFn: () =>
            getMonthlyAllocatedRevenue({
                gte: filters.gte,
                lte: filters.lte,
                employee_id: filters.employee_id,
            }),
        enabled: !!filters.gte && !!filters.lte,
    })

    // Generate month columns from date range
    const monthColumns = useMemo(() => {
        if (!filters.gte || !filters.lte) return []
        const start = dayjs(filters.gte)
        const end = dayjs(filters.lte)
        const months: string[] = []
        let current = start.startOf('month')
        while (current.isBefore(end) || current.isSame(end, 'month')) {
            months.push(current.format('YYYY-MM'))
            current = current.add(1, 'month')
        }
        return months
    }, [filters.gte, filters.lte])

    // Build employee monthly breakdown lookup
    const monthlyBreakdownMap = useMemo(() => {
        const map = new Map<number, MonthlyAllocatedRevenue>()
        monthlyAllocatedData?.forEach((emp) => {
            map.set(emp.employee_id, emp)
        })
        return map
    }, [monthlyAllocatedData])

    // Period labels
    const currentPeriodLabel = useMemo(() => {
        if (!filters.gte || !filters.lte) return ''
        return `${dayjs(filters.gte).format('MM/YYYY')} - ${dayjs(filters.lte).format('MM/YYYY')}`
    }, [filters.gte, filters.lte])

    const comparePeriodLabel = useMemo(() => {
        if (!compareRange.gte || !compareRange.lte) return ''
        return `${dayjs(compareRange.gte).format('MM/YYYY')} - ${dayjs(compareRange.lte).format('MM/YYYY')}`
    }, [compareRange.gte, compareRange.lte])

    // Helper to get comparison employee data
    const getComparisonEmployee = (employeeId: number) => {
        return comparisonData?.employees?.find(
            (emp) => emp.employee_id === employeeId
        )
    }

    // Helper to calculate and render change percentage
    const renderChangeIndicator = (
        current: number,
        previous: number | undefined
    ) => {
        if (!hasComparison || previous === undefined || previous === 0)
            return null
        const changePercent = ((current - previous) / previous) * 100
        const isPositive = changePercent >= 0
        return (
            <span
                className={`text-xs ml-1 ${isPositive ? 'text-green-600' : 'text-red-600'}`}
            >
                ({isPositive ? '+' : ''}
                {changePercent.toFixed(1)}%)
            </span>
        )
    }

    // Handle export to Excel via API
    const [isExporting, setIsExporting] = useState(false)
    const handleExportExcel = async () => {
        try {
            setIsExporting(true)
            const blob = await exportEmployeePerformances({
                gte: filters.gte,
                lte: filters.lte,
                employee_id: filters.employee_id,
            })
            const filename = `employee-performance-${dayjs().format('YYYY-MM-DD')}.xlsx`
            const url = window.URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.download = filename
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            window.URL.revokeObjectURL(url)
        } catch (error) {
            console.error('Export error:', error)
        } finally {
            setIsExporting(false)
        }
    }

    // Table columns - transposed (employees as rows, metrics as columns)
    const columns = usePerformanceTableColumns({
        selectedRevenueTypes,
        getComparisonEmployee,
        renderChangeIndicator,
        monthColumns,
        monthlyBreakdownMap,
        viewMode,
    })

    // Footer row with totals
    const footerRow = useMemo(() => {
        const employees = data?.employees || []
        const totals = employees.reduce(
            (acc, emp) => ({
                target_amount: acc.target_amount + (emp.target_amount || 0),
                actual_revenue: acc.actual_revenue + (emp.actual_revenue || 0),
                renewal_revenue:
                    acc.renewal_revenue + (emp.renewal_revenue || 0),
                new_sale_revenue:
                    acc.new_sale_revenue + (emp.new_sale_revenue || 0),
                upsell_revenue: acc.upsell_revenue + (emp.upsell_revenue || 0),
            }),
            {
                target_amount: 0,
                actual_revenue: 0,
                renewal_revenue: 0,
                new_sale_revenue: 0,
                upsell_revenue: 0,
            }
        )

        // Calculate total achievement
        let selectedRevenue = 0
        if (selectedRevenueTypes.includes('renewal')) {
            selectedRevenue += totals.renewal_revenue
        }
        if (selectedRevenueTypes.includes('new_sale')) {
            selectedRevenue += totals.new_sale_revenue
        }
        if (selectedRevenueTypes.includes('upsell')) {
            selectedRevenue += totals.upsell_revenue
        }
        const totalAchievement =
            totals.target_amount > 0
                ? (selectedRevenue / totals.target_amount) * 100
                : 0

        const cells: Record<string, React.ReactNode> = {
            employee_name: t('overview.total'),
            target_amount: formatCurrency(totals.target_amount),
            actual_revenue: formatCurrency(selectedRevenue),
            renewal_revenue: formatCurrency(totals.renewal_revenue),
            new_sale_revenue: formatCurrency(totals.new_sale_revenue),
            upsell_revenue: formatCurrency(totals.upsell_revenue),
            achievement_percentage: (
                <span
                    className={`font-semibold ${
                        totalAchievement >= 100
                            ? 'text-green-600'
                            : totalAchievement >= 80
                              ? 'text-yellow-600'
                              : 'text-red-600'
                    }`}
                >
                    {formatNumeral(Number(totalAchievement.toFixed(2)))}%
                </span>
            ),
        }

        // Monthly totals
        monthColumns.forEach((month) => {
            let monthTotal = 0
            monthlyBreakdownMap.forEach((empData) => {
                const breakdown = empData.monthly_breakdown?.find(
                    (b) => b.revenue_month === month
                )
                if (breakdown) {
                    if (selectedRevenueTypes.includes('renewal'))
                        monthTotal += breakdown.renewal_revenue || 0
                    if (selectedRevenueTypes.includes('new_sale'))
                        monthTotal += breakdown.new_sale_revenue || 0
                    if (selectedRevenueTypes.includes('upsell'))
                        monthTotal += breakdown.upsell_revenue || 0
                }
            })
            cells[`month_${month}`] = formatCurrency(monthTotal)
        })

        return {
            cells,
            cellClassName: 'text-right',
        }
    }, [data, selectedRevenueTypes, t, monthColumns, monthlyBreakdownMap])

    const renderTopComponent = renderTopState()
    if (renderTopComponent) return renderTopComponent

    return (
        <div className="space-y-2 mt-2">
            {/* Header Table - Employee Performance */}
            <Paper className="space-y-2">
                <div className="flex items-center justify-between">
                    <Typogaphy className="font-medium capitalize text-primary">
                        {t('employee_performance')}
                        {hasComparison && (
                            <Typogaphy
                                size="small"
                                className="text-gray-600 font-normal"
                            >
                                {currentPeriodLabel} so với {comparePeriodLabel}
                            </Typogaphy>
                        )}
                    </Typogaphy>

                    {/* Comparison selector */}
                    <div className="flex items-center gap-2">
                        {!showCompareSelector ? (
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={handleAddComparison}
                            >
                                <Calendar className="w-4 h-4 mr-1" />
                                {t('common.compare')}
                            </Button>
                        ) : (
                            <div className="flex items-center gap-2">
                                <Button
                                    size="small"
                                    variant="text"
                                    onClick={handleRemoveComparison}
                                    className="!text-red-500"
                                >
                                    {t('common.cancel')}
                                </Button>
                                <MonthRangeSelector
                                    value={
                                        compareRange.gte && compareRange.lte
                                            ? {
                                                  gte: compareRange.gte,
                                                  lte: compareRange.lte,
                                              }
                                            : undefined
                                    }
                                    onChange={(value) =>
                                        setCompareRange({
                                            gte: value?.gte || null,
                                            lte: value?.lte || null,
                                        })
                                    }
                                />
                            </div>
                        )}
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={handleExportExcel}
                            disabled={isExporting}
                            startIcon={<Download className="w-4 h-4" />}
                        >
                            {t('common.export')}
                        </Button>
                    </div>
                </div>
                {/* Revenue type filters + view mode toggle */}
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1 text-xs">
                        {[
                            {
                                key: 'renewal' as RevenueType,
                                label: t('employees.performance.renewal'),
                            },
                            {
                                key: 'new_sale' as RevenueType,
                                label: t('employees.performance.new_sale'),
                            },
                            {
                                key: 'upsell' as RevenueType,
                                label: t('employees.performance.upsell'),
                            },
                        ].map(({ key, label }) => {
                            const isActive = selectedRevenueTypes.includes(key)
                            return (
                                <button
                                    key={key}
                                    type="button"
                                    onClick={() =>
                                        setSelectedRevenueTypes((prev) =>
                                            isActive
                                                ? prev.filter((t) => t !== key)
                                                : [...prev, key]
                                        )
                                    }
                                    className={`px-2 py-0.5 rounded border transition-colors ${
                                        isActive
                                            ? 'border-primary/30 bg-primary/5 text-foreground'
                                            : 'border-transparent bg-muted/50 text-muted-foreground line-through'
                                    }`}
                                >
                                    {label}
                                </button>
                            )
                        })}
                    </div>
                    <div className="flex items-center gap-0.5 text-xs">
                        <button
                            type="button"
                            onClick={() => setViewMode('numbers')}
                            className={`p-1 rounded transition-colors ${
                                viewMode === 'numbers'
                                    ? 'bg-primary/10 text-primary'
                                    : 'text-muted-foreground hover:text-foreground'
                            }`}
                            title="Numbers"
                        >
                            <Hash className="w-3.5 h-3.5" />
                        </button>
                        <button
                            type="button"
                            onClick={() => setViewMode('progress')}
                            className={`p-1 rounded transition-colors ${
                                viewMode === 'progress'
                                    ? 'bg-primary/10 text-primary'
                                    : 'text-muted-foreground hover:text-foreground'
                            }`}
                            title="Progress"
                        >
                            <BarChart3 className="w-3.5 h-3.5" />
                        </button>
                    </div>
                </div>
                {/* Table with DataTableCustomized */}
                <DataTableCustomized
                    columns={columns}
                    rows={data?.employees || []}
                    rowKey={(row) => String(row.employee_id)}
                    footerRow={footerRow}
                    showIndex
                    size="small"
                />
                <AchievementByPaymentDate
                    {...filters}
                    revenueTypes={selectedRevenueTypes}
                />
            </Paper>
            {/* Charts Section */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <ActualTarget
                    filters={{
                        gte: filters.gte,
                        lte: filters.lte,
                        employee_id: filters.employee_id,
                    }}
                />
                <Paper className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <DensityTargetChart
                        filters={{
                            gte: filters.gte,
                            lte: filters.lte,
                            employee_id: filters.employee_id,
                        }}
                        revenueTypes={selectedRevenueTypes}
                    />
                    <DensityChart
                        filters={{
                            gte: filters.gte,
                            lte: filters.lte,
                            employee_id: filters.employee_id,
                        }}
                        revenueTypes={selectedRevenueTypes}
                    />
                </Paper>
            </div>
            <RevenueTarget {...filters} />
            <RevenueByPaymentDate {...filters} />
            <ServiceBreakdown
                filters={{
                    gte: filters.gte,
                    lte: filters.lte,
                    employee_id: filters.employee_id,
                }}
            />
            <MonthlyRevenueTrends
                filters={{
                    gte: filters.gte,
                    lte: filters.lte,
                    employee_id: filters.employee_id,
                }}
            />
            <MonthlyServiceBreakdown
                filters={{
                    gte: filters.gte,
                    lte: filters.lte,
                    employee_id: filters.employee_id,
                }}
            />
        </div>
    )
}

export default Dashboard
